'use client';

import { useEffect, useState } from 'react';
import {
  DEMO_TEAM,
  getOnlineUsers,
  loadAvailabilities,
  relativeLastSeen,
  setMyAvailability,
  statusColorVar,
  statusEmoji,
  statusLabel,
  type AvailabilityStatus,
  type UserAvailability,
} from '@/src/lib/user-availability';

export function AvailabilityIndicator({ currentUserId }: { currentUserId: string }) {
  const [users, setUsers] = useState<UserAvailability[]>(() => {
    const stored = loadAvailabilities();
    if (stored.length > 0) return stored;
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('kontora_user_availability_v1', JSON.stringify(DEMO_TEAM));
      } catch {}
    }
    return DEMO_TEAM;
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setUsers(loadAvailabilities());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const online = getOnlineUsers(users);
  const onlineCount = online.length;

  return (
    <div className="relative" data-tour="availability-indicator">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Team-Verfügbarkeit"
        aria-expanded={open}
        aria-haspopup="dialog"
        data-tour="availability-toggle"
        className="inline-flex h-9 items-center gap-1 rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-2 text-xs text-[var(--k-color-text-secondary)]"
      >
        <span style={{ color: 'var(--k-color-success-strong)' }}>●</span>
        <span>
          <strong>{onlineCount}</strong> online
        </span>
      </button>

      {open ? (
        <div
          data-tour="availability-popover"
          className="absolute right-0 top-11 z-30 w-80 rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-xl"
        >
          <header className="border-b border-[var(--k-color-border-subtle)] p-3">
            <p className="text-sm font-semibold">Team-Verfügbarkeit</p>
            <p className="text-[10px] text-[var(--k-color-text-muted)]">
              {onlineCount} von {users.length} online
            </p>
          </header>

          <ul className="max-h-80 overflow-y-auto p-2">
            {users.map((user) => {
              const isMe = user.userId === currentUserId;
              return (
                <li
                  key={user.userId}
                  data-tour={`availability-${user.userId}`}
                  className="rounded-md p-2 hover:bg-[var(--k-color-surface-muted)]"
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--k-color-accent)] text-xs font-bold text-white">
                        {user.userName
                          .split(' ')
                          .map((s) => s[0] ?? '')
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()}
                      </div>
                      <span
                        className="absolute -bottom-0.5 -right-0.5 inline-flex h-3 w-3 rounded-full border-2 border-[var(--k-color-surface)]"
                        style={{ background: statusColorVar(user.status) }}
                        title={statusLabel(user.status)}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold">
                          {user.userName}{' '}
                          {isMe ? (
                            <span className="text-[10px] text-[var(--k-color-text-muted)]">(Du)</span>
                          ) : null}
                        </p>
                        <span className="text-xs">{statusEmoji(user.status)}</span>
                      </div>
                      <p className="text-[10px] text-[var(--k-color-text-muted)]">{user.email}</p>
                      {user.statusText ? (
                        <p className="mt-0.5 text-xs italic text-[var(--k-color-text-secondary)]">
                          {user.statusText}
                        </p>
                      ) : null}
                      {user.currentActivity ? (
                        <p className="mt-0.5 text-[10px] text-[var(--k-color-text-link)]">
                          → {user.currentActivity}
                        </p>
                      ) : null}
                      <p className="mt-0.5 text-[10px] text-[var(--k-color-text-muted)]">
                        {relativeLastSeen(user.lastSeen)}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <footer className="border-t border-[var(--k-color-border-subtle)] p-2">
            <p className="text-[10px] text-[var(--k-color-text-muted)]">
              Klicke einen Status an, um deinen zu ändern:
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {(['online', 'busy', 'away', 'dnd', 'offline'] as const).map((s) => (
                <MyStatusButton
                  key={s}
                  status={s}
                  userId={currentUserId}
                  onChange={() => setUsers(loadAvailabilities())}
                />
              ))}
            </div>
          </footer>
        </div>
      ) : null}
    </div>
  );
}

function MyStatusButton({
  status,
  userId,
  onChange,
}: {
  status: AvailabilityStatus;
  userId: string;
  onChange: () => void;
}) {
  function handleClick() {
    const me =
      loadAvailabilities().find((u) => u.userId === userId) ?? DEMO_TEAM.find((u) => u.userId === userId);
    if (!me) return;
    setMyAvailability(userId, me.userName, me.email, status, me.statusText, me.currentActivity);
    onChange();
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      data-tour={`set-status-${status}`}
      className="rounded-full border px-2 py-0.5 text-[10px] hover:bg-[var(--k-color-surface-muted)]"
    >
      {statusEmoji(status)} {statusLabel(status)}
    </button>
  );
}
